import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getErrorMessage,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { SIGNED_DOCUMENTS } from '../constants/signed-documents'
import {
    deleteSignedDocumentImage,
    getSignedDocumentImages,
    uploadSignedDocumentImages,
} from '../services'
import { SignedDocumentImage, SignedDocumentType } from '../types'
import { useSignedDocumentErrorText } from './use-signed-document-error-text'

/**
 * Hình upload cho trang "Hình ảnh" của chứng từ (chỉ loại `hasImages`, hiện là biên bản
 * đối soát). Upload / xoá trả về danh sách mới nên ghi thẳng vào cache; bản xem trước dùng
 * image_source = upload phải dựng lại. Hình của mỗi loại chứng từ độc lập.
 */
export const useSignedDocumentImages = (
    type: SignedDocumentType,
    invoiceID: number,
    enabled = true
) => {
    const queryClient = useQueryClient()
    const errorText = useSignedDocumentErrorText(type)
    const queryKey = [
        QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_IMAGES,
        type,
        invoiceID,
    ]

    const query = useQueryWithHandler<SignedDocumentImage[]>(
        {
            queryKey: [
                QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_IMAGES,
                type,
                invoiceID,
            ],
            queryFn: async () =>
                (await getSignedDocumentImages(type, invoiceID))?.result ?? [],
            enabled: enabled && SIGNED_DOCUMENTS[type].hasImages,
            // url presigned hết hạn sau 1 giờ: mỗi lần mở popup lấy lại
            gcTime: 0,
        },
        { showLoading: false, showError: false }
    )

    const storeImages = (images?: SignedDocumentImage[]) => {
        queryClient.setQueryData(queryKey, images ?? [])
        queryClient.invalidateQueries({
            queryKey: [
                QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_PREVIEW,
                type,
                invoiceID,
                'upload',
            ],
        })
    }

    const handlers = {
        reactQueryKeys: QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_IMAGES,
        // useMutationWithHandler không tra namespace invoice: tự toast bằng errorText
        showErrorToast: false,
        onError: (error: Error) => {
            toast.error(errorText(getErrorMessage(error)))
            // Danh sách có thể đã đổi (vd hình đã bị người khác xoá): đọc lại
            query.refetch()
        },
    }

    const upload = useMutationWithHandler(
        { mutationFn: uploadSignedDocumentImages },
        {
            ...handlers,
            showSuccessToast: false,
            onSuccess: (response) => storeImages(response?.result),
        }
    )

    const remove = useMutationWithHandler(
        { mutationFn: deleteSignedDocumentImage },
        {
            ...handlers,
            showSuccessToast: false,
            onSuccess: (response) => storeImages(response?.result),
        }
    )

    return {
        images: query.data ?? [],
        isLoading: query.isLoading,
        isFetched: query.isFetched,
        upload: (files: File[]) =>
            upload.mutateAsync({ type, invoiceID, files }),
        isUploading: upload.isPending,
        remove: (imageID: number) =>
            remove.mutateAsync({ type, invoiceID, imageID }),
        removingID: remove.isPending ? remove.variables?.imageID : undefined,
    }
}
